import { ref, computed } from 'vue';
import { getUserPackageTypeApi, getAccountBasicDataApi, getDraftingPlanApi } from '@/apis/user';
import { getSubscriptionsApi, type PaymentSubscription } from '@/apis/payment';

import { headerService } from '@/services/header';
import type { IPackageType, UserPackageVersion } from '@/types/user';

const packageInfo = ref<IPackageType[]>([]);
const planType = ref<string>('');
const accountBasicData = ref<Record<string, any> | null>(null);
const subscriptions = ref<PaymentSubscription[]>([]);
const isLoading = ref(false);

const isInitialized = ref(false);
const maxPriceIds = new Set([
    import.meta.env.VITE_STRIPE_PRICE_ID_MAX_MONTHLY,
    import.meta.env.VITE_STRIPE_PRICE_ID_MAX_ANNUAL,
].filter(Boolean));

function isMaxSubscriptionItem(item: PaymentSubscription['items'][number]) {
    const priceId = item.price_id || item.stripe_price_id;
    return maxPriceIds.has(priceId);
}

// 开发环境下强制设置版本（用于预览）
// 在浏览器控制台输入: window.__FORCE_PACKAGE_VERSION = 'pro'、'max' 或 'free'
const forceVersion = ref<UserPackageVersion | null>(null);

export function setDraftingPlanType(type?: string | null) {
    planType.value = (type || '').toLowerCase();
}

// 监听全局变量变化
if (typeof window !== 'undefined') {
    Object.defineProperty(window, '__FORCE_PACKAGE_VERSION', {
        set: (value: UserPackageVersion | null) => {
            forceVersion.value = value;
        },
        get: () => forceVersion.value,
    });
}

/**
 * 用户套餐管理 Composable
 */
export function useUserPackage() {
    /**
     * 获取用户套餐信息
     */
    async function fetchPackageInfo(options?: { refresh?: boolean }) {
        if (isLoading.value) return;

        isLoading.value = true;
        try {
            const [packageResult, accountResult, subscriptionResult, draftingPlanResult] = await Promise.allSettled([
                getUserPackageTypeApi(options),
                getAccountBasicDataApi(),
                getSubscriptionsApi(),
                getDraftingPlanApi(),
            ]);
            packageInfo.value = packageResult.status === 'fulfilled' ? packageResult.value : [];
            accountBasicData.value = accountResult.status === 'fulfilled' ? accountResult.value?.data || null : null;
            subscriptions.value = subscriptionResult.status === 'fulfilled' ? subscriptionResult.value || [] : [];
            // 后端返回首字母大写（Free/Pro/Max/Internal），转换为小写存储
            setDraftingPlanType(draftingPlanResult.status === 'fulfilled' ? draftingPlanResult.value?.plan_type : '');
            isInitialized.value = true;
        } catch (error) {
            console.error('Failed to fetch user package or account info:', error);
            packageInfo.value = [];
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * 判断用户是否为专业版（Pro）
     * 基于 /api/user/drafting-plan 接口返回的 plan_type 字段
     */
    const isPro = computed(() => {
        return planType.value === 'pro';
    });

    /**
     * 判断用户是否为 Max 版
     * 基于 /api/user/drafting-plan 接口返回的 plan_type 字段
     */
    const isMax = computed(() => {
        return planType.value === 'max';
    });

    /**
     * 判断用户是否为免费版
     * 基于 /api/user/drafting-plan 接口返回的 plan_type 字段
     */
    const isFreemium = computed(() => {
        return planType.value === 'free';
    });

    /**
     * 判断用户是否为试用版
     */
    const isTrial = computed(() => {
        return packageInfo.value.some(pkg =>
            pkg.package_type === 'TRIAL',
        );
    });

    /**
     * 获取当前用户的套餐版本标识
     */
    const packageVersion = computed<UserPackageVersion>(() => {
        // 开发环境优先使用强制版本
        if (forceVersion.value) {
            return forceVersion.value;
        }
        if (isMax.value) return 'max';
        return isPro.value ? 'pro' : 'free';
    });

    /**
     * 获取版本显示文本
     */
    const packageVersionText = computed(() => {
        if (isMax.value) return 'Max';
        return isPro.value ? 'Pro' : 'Free';
    });

    const hasPaymentCustomerId = computed(() => {
        const account = accountBasicData.value as any;
        const headerAccount = headerService.account as any;
        return !!account?.payment_customer_id || !!headerAccount?.payment_customer_id;
    });

    const isEnterpriseUser = computed(() => {
        const account = accountBasicData.value as any;
        const headerAccount = headerService.account as any;
        return !!account?.company_id || !!headerAccount?.company_id;
    });

    /**
     * 有效订阅状态集合
     */
    const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing'];

    /**
     * 查找 Drafting Pro 的有效订阅记录
     */
    const activeProSubscription = computed(() => {
        return subscriptions.value.find(sub =>
            ACTIVE_SUBSCRIPTION_STATUSES.includes(sub.subscription_status) &&
            (sub.items || []).some(item =>
                item.product_name?.toLowerCase().trim() === 'drafting pro',
            ),
        ) || null;
    });

    /**
     * 查找 Drafting Max 的有效订阅记录
     */
    const activeMaxSubscription = computed(() => {
        return subscriptions.value.find(sub =>
            ACTIVE_SUBSCRIPTION_STATUSES.includes(sub.subscription_status) &&
            (sub.items || []).some(isMaxSubscriptionItem),
        ) || null;
    });

    /**
     * 是否有 Drafting 付费套餐的有效订阅（active 或 trialing）
     */
    const hasActiveSubscription = computed(() => {
        return !!activeProSubscription.value || !!activeMaxSubscription.value;
    });

    /**
     * 是否处于试用期
     */
    const isTrialing = computed(() => {
        return activeProSubscription.value?.subscription_status === 'trialing'
            || activeMaxSubscription.value?.subscription_status === 'trialing';
    });

    /**
     * 试用期结束时间
     */
    const trialEndDate = computed(() => {
        if (!isTrialing.value) return null;
        return activeMaxSubscription.value?.current_period_end
            || activeProSubscription.value?.current_period_end
            || null;
    });

    /**
     * 初始化套餐信息（如果尚未初始化）
     */
    async function init() {
        if (!isInitialized.value) {
            await fetchPackageInfo();
        }
    }

    return {
        packageInfo,
        isLoading,
        isInitialized,
        isPro,
        isMax,
        isFreemium,
        isTrial,
        hasPaymentCustomerId,
        hasActiveSubscription,
        isEnterpriseUser,
        isTrialing,
        trialEndDate,
        activeProSubscription,
        activeMaxSubscription,
        packageVersion,
        packageVersionText,
        planType,
        fetchPackageInfo,
        subscriptions,
        init,
    };
}
